import React, { useEffect } from 'react';
import { ArrowDown, Check, Clock, Video, ShieldCheck, Star } from 'lucide-react';
import { CONSULTATION as C, consultationSchema } from '../consultation';
import { SERVICE_PAGES, IMG } from '../servicePages';
import PortfolioGrid from '../components/PortfolioGrid';
import { ServiceFAQ } from '../components/ServiceSections';
import { ConsultationForm } from '../components/ConsultationForm';
import useSEO from '../hooks/useSEO';

const scrollToForm = () => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });

const Hero = () => (
  <section className="relative pt-32 pb-14 lg:pt-36 lg:pb-16 overflow-hidden">
    <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#F5CD1B]/6 blur-[140px] -z-10"></div>
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <p className="section-label mb-6" data-testid="consult-eyebrow">Free Marketing Health Check · {C.month} · Online</p>
        <h1 data-testid="consult-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white">
          {C.headline.replace(C.yellow, '')}<span className="text-[#F5CD1B]">{C.yellow}</span>
        </h1>
        {C.intro.map((p) => <p key={p} className="text-white/70 text-base md:text-lg leading-relaxed mt-6 max-w-2xl">{p}</p>)}
        <div className="flex flex-wrap items-center gap-4 mt-8">
          <span data-testid="consult-deadline" className="text-white/60 text-sm">Applications close {C.deadlineLabel}</span>
        </div>
        <button onClick={scrollToForm} data-testid="consult-hero-apply-btn" className="btn-yellow inline-flex items-center gap-2 px-6 py-4 rounded-md uppercase text-sm mt-8">
          Apply for your free health check <ArrowDown size={16} />
        </button>
      </div>
      <div className="lg:col-span-6">
        <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[2000/1473]">
          <img src={IMG.strategyConsult} alt="Yellow directors carrying out a free Marketing Health Check" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  </section>
);

const UrgencyStrip = () => (
  <section className="bg-[#F5CD1B] text-[#0b0b0d]">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
      <p className="font-display font-bold text-lg md:text-xl" data-testid="consult-urgency">
        Usually {C.usualPrice}. Free for just 15 organisations in {C.month}. Applications close {C.deadlineLabel}.
      </p>
      <button onClick={scrollToForm} data-testid="consult-strip-apply-btn" className="shrink-0 px-6 py-3 rounded-md bg-[#0b0b0d] text-white uppercase text-sm font-bold hover:bg-black transition-colors">
        Claim your space
      </button>
    </div>
  </section>
);

const Included = () => (
  <section className="py-14 lg:py-16">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
      <p className="section-label mb-4">What you get</p>
      <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05] max-w-3xl mb-8">What your health check includes.</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {C.included.map((item, i) => (
          <div key={item.title} data-testid={`consult-included-${i}`} className="bg-[#111114] border border-white/10 rounded-2xl p-7 hover:border-[#F5CD1B]/30 transition-colors">
            <div className="w-8 h-8 rounded-md bg-[#F5CD1B] flex items-center justify-center"><Check size={16} className="text-[#0b0b0d]" strokeWidth={3} /></div>
            <h3 className="font-display text-xl font-bold text-white mt-5">{item.title}</h3>
            <p className="text-white/60 text-sm mt-3 leading-relaxed">{item.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="py-14 lg:py-16 bg-[#08080a]">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
      <p className="section-label mb-4">How it works</p>
      <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05] mb-8">Four simple steps.</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {C.steps.map((step, i) => (
          <div key={step.title} className="bg-[#111114] border border-white/10 rounded-2xl p-8">
            <div className="text-[#F5CD1B] font-display text-5xl font-bold">0{i + 1}</div>
            <h3 className="font-display text-2xl font-bold text-white mt-4">{step.title}</h3>
            <p className="text-white/60 text-sm mt-3 leading-relaxed">{step.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const NoSelling = () => (
  <section className="py-14 lg:py-16">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-[#111114] border border-[#F5CD1B]/30 rounded-2xl p-10">
        <ShieldCheck size={32} className="text-[#F5CD1B]" />
        <h2 className="font-display text-3xl md:text-4xl font-bold text-white mt-5" data-testid="consult-promise">{C.promise.title}</h2>
        <p className="text-white/70 text-base md:text-lg mt-4 leading-relaxed">{C.promise.body}</p>
        <div className="flex flex-wrap gap-6 mt-8 text-white/80 text-sm">
          <span className="inline-flex items-center gap-2"><Video size={16} className="text-[#F5CD1B]" /> Online video call</span>
          <span className="inline-flex items-center gap-2"><Clock size={16} className="text-[#F5CD1B]" /> Up to 2 hours</span>
          <span className="inline-flex items-center gap-2"><Check size={16} className="text-[#F5CD1B]" /> Worth {C.usualPrice}</span>
        </div>
      </div>
      <div className="bg-[#111114] border border-white/10 rounded-2xl p-10">
        <div className="flex gap-1">{[0, 1, 2, 3, 4].map((i) => <Star key={i} size={18} className="text-[#F5CD1B] fill-[#F5CD1B]" />)}</div>
        <h3 className="font-display text-2xl font-bold text-white mt-5">One small favour</h3>
        <p className="text-white/70 mt-4 leading-relaxed">{C.promise.review}</p>
      </div>
    </div>
  </section>
);

const FreeConsultation = () => {
  useSEO({ title: C.metaTitle, description: C.metaDescription, path: `/${C.slug}`, image: IMG.strategyConsult });

  useEffect(() => {
    const script = document.createElement('script');
    script.id = 'service-jsonld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(consultationSchema());
    document.getElementById('service-jsonld')?.remove();
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  return (
    <>
      <Hero />
      <UrgencyStrip />
      <Included />
      <Steps />
      <NoSelling />
      <section className="py-10 lg:py-12" data-testid="consult-videos">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <p className="section-label mb-4">Watch</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05] mb-8">Listen to some free marketing advice from <span className="text-[#F5CD1B]">David Roberts</span>, who you will meet in your health check.</h2>
          <PortfolioGrid items={SERVICE_PAGES['marketing-strategy'].videos} cols={2} />
        </div>
      </section>
      <section id="apply" className="py-14 lg:py-16 bg-[#08080a] scroll-mt-24">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05]">Apply before the spaces run out.</h2>
            <p className="text-white/70 mt-4 max-w-2xl mx-auto">Only 15 free health checks are available in {C.month}, and applications close on {C.deadlineLabel}. Apply now to avoid missing out.</p>
          </div>
          <ConsultationForm />
        </div>
      </section>
      <ServiceFAQ s={{ name: 'Marketing Health Check', faqs: C.faqs }} compact />
    </>
  );
};

export default FreeConsultation;
